import { useEffect, useState } from "react";
import { supabase } from "@/services/supabase";
import { SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from "@/config/publicConfig";

interface PendingProfile {
  id: string;
  id_document_type: string;
  id_front_url: string | null;
  id_back_url: string | null;
  created_at: string;
}

export function IdentityReviewPage() {
  const [profiles, setProfiles] = useState<PendingProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllWithDocs, setShowAllWithDocs] = useState(false);
  const [activeImage, setActiveImage] = useState<{
    url: string;
    title: string;
  } | null>(null);
  const [supabaseDebug, setSupabaseDebug] = useState<{
    url?: string;
    keyPresent: boolean;
  } | null>(null);

  // 检查是否通过了前端的 admin 登录
  const isAuthenticated =
    sessionStorage.getItem("admin_authenticated") === "true";

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // 获取环境变量或回退到硬编码值
      const envUrl =
        (import.meta as any)?.env?.VITE_SUPABASE_URL ||
        "https://dyhawqwzcxpalgdizcvx.supabase.co";
      const envKey =
        (import.meta as any)?.env?.VITE_SUPABASE_PUBLISHABLE_KEY ||
        "eyJhbGciOiJIUzI...";

      if (!cancelled) {
        setSupabaseDebug({
          url: envUrl,
          keyPresent: Boolean(envKey),
        });
      }

      if (!isAuthenticated) {
        if (!cancelled) {
          setError("请先从 /admin 登录");
          setLoading(false);
        }
        return;
      }

      // 重要：不要只依赖 sessionStorage。RPC 的权限校验依赖 Supabase Auth 会话（auth.uid()）。
      const { data, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !data.session) {
        sessionStorage.removeItem("admin_authenticated");
        if (!cancelled) {
          setError(
            "管理员会话已失效，请重新登录（否则“通过/拒绝”RPC 会被数据库拒绝）。",
          );
          setLoading(false);
        }
        return;
      }

      if (!cancelled) {
        fetchPendingProfiles();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const fetchPendingProfiles = async () => {
    setLoading(true);
    setError(null);
    // Fetch users whose status is submitted
    const baseQuery = supabase
      .from("profiles")
      .select(
        "id, id_document_type, id_front_url, id_back_url, created_at, age_verified_status",
      )
      .order("created_at", { ascending: false });

    const { data, error } = showAllWithDocs
      ? await baseQuery.not("id_front_url", "is", null)
      : await baseQuery.eq("age_verified_status", "submitted");

    if (error) {
      console.error("Error fetching pending profiles:", error);
      setProfiles([]);
      setError(`${error.message}${error.details ? `\n${error.details}` : ""}`);
    } else {
      setProfiles(data || []);
    }
    setLoading(false);
  };

  const handleUpdateStatus = async (
    id: string,
    status: "approved" | "rejected",
  ) => {
    setError(null);
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      sessionStorage.removeItem("admin_authenticated");
      setError("管理员会话已失效，请重新登录后再操作。");
      return;
    }

    const res = await fetch(
      `${SUPABASE_URL}/functions/v1/admin-finalize-age-review`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionData.session.access_token}`,
          apikey: SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({
          target_user_id: id,
          status,
        }),
      },
    );

    const payload = await res.json().catch(() => ({}));

    if (!res.ok) {
      setError(
        `更新失败：\n${typeof payload?.error === "string" ? payload.error : JSON.stringify(payload)}`,
      );
    } else {
      alert(
        `已${status === "approved" ? "通过" : "拒绝"} / Status updated to ${status}`,
      );
      fetchPendingProfiles();
    }
  };

  const getImageUrl = (path: string | null | undefined) => {
    return path || "";
  };

  if (loading)
    return <div className="p-8 text-center">加载中 / Loading...</div>;

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-8">身份验证审核 (Admin Review)</h1>

      {activeImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={activeImage.title}
          onClick={() => setActiveImage(null)}
        >
          <div
            className="relative max-w-5xl w-full max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute -top-12 left-0 right-0 flex items-center justify-between text-white">
              <div className="text-sm opacity-90 truncate pr-4">
                {activeImage.title}
              </div>
              <button
                className="px-3 py-1 rounded bg-white/10 hover:bg-white/20"
                onClick={() => setActiveImage(null)}
              >
                关闭
              </button>
            </div>
            <img
              src={activeImage.url}
              alt={activeImage.title}
              className="w-full h-full max-h-[90vh] object-contain bg-black rounded"
            />
          </div>
        </div>
      )}

      <div className="mb-6 p-4 rounded-lg border bg-gray-50 text-sm space-y-2">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="text-gray-700">
            <span className="font-medium">当前环境:</span>{" "}
            {supabaseDebug?.url || "(未注入 VITE_SUPABASE_URL)"}
          </div>
          <div className="text-gray-700">
            <span className="font-medium">Key:</span>{" "}
            {supabaseDebug?.keyPresent ? "已配置" : "未配置"}
          </div>
          <div className="text-gray-700">
            <span className="font-medium">登录状态:</span>{" "}
            {isAuthenticated ? "已登录" : "未登录"}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-gray-700">
            <input
              type="checkbox"
              checked={showAllWithDocs}
              onChange={(e) => setShowAllWithDocs(e.target.checked)}
            />
            显示所有已上传资料（不只 submitted）
          </label>
          <button
            className="px-3 py-1 rounded bg-white border hover:bg-gray-100"
            onClick={fetchPendingProfiles}
          >
            刷新
          </button>
        </div>
        {error && (
          <pre className="whitespace-pre-wrap text-red-600 bg-red-50 border border-red-100 rounded p-3">
            {error}
          </pre>
        )}
        {!isAuthenticated ? (
          <div className="text-gray-600">
            <p className="mb-2">
              请先登录后再审核。
            </p>
            <a
              href="/admin/login"
              className="inline-block px-4 py-2 bg-pink-600 text-white rounded hover:bg-pink-700"
            >
              去登录
            </a>
          </div>
        ) : null}
      </div>

      {profiles.length === 0 ? (
        <div className="text-gray-500">
          目前没有待审核的身份信息。 / No pending reviews.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {profiles.map((profile) => (
            <div
              key={profile.id}
              className="bg-white p-6 rounded-lg shadow-md border border-gray-200"
            >
              <h2 className="text-xl font-semibold mb-2">
                用户ID: {profile.id.substring(0, 8)}...
              </h2>
              <p className="text-sm text-gray-500 mb-4">完整ID: {profile.id}</p>
              <p className="mb-4">
                <span className="font-medium">证件类型:</span>{" "}
                {profile.id_document_type}
              </p>

              <div className="space-y-4 mb-6">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    正面 (Front)
                  </p>
                  {profile.id_front_url ? (
                    <button
                      type="button"
                      className="block w-full"
                      onClick={() =>
                        setActiveImage({
                          url: getImageUrl(profile.id_front_url),
                          title: `证件正面（${profile.id}）`,
                        })
                      }
                    >
                      <img
                        src={getImageUrl(profile.id_front_url)}
                        alt="ID Front"
                        className="w-full h-48 object-contain bg-gray-100 rounded border hover:opacity-90 transition-opacity"
                        onError={(e) =>
                          (e.currentTarget.src =
                            "https://via.placeholder.com/400x200?text=Image+Load+Error")
                        }
                      />
                    </button>
                  ) : (
                    <div className="w-full h-48 bg-gray-100 flex items-center justify-center rounded border text-center text-sm text-gray-600 px-2">
                      未保存证件图（仅 Telegram 投递）。请到 Telegram 审核群查看上方机器人发送的图片。
                    </div>
                  )}
                </div>

                {profile.id_back_url && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">
                      反面 (Back)
                    </p>
                    <button
                      type="button"
                      className="block w-full"
                      onClick={() =>
                        setActiveImage({
                          url: getImageUrl(profile.id_back_url),
                          title: `证件反面（${profile.id}）`,
                        })
                      }
                    >
                      <img
                        src={getImageUrl(profile.id_back_url)}
                        alt="ID Back"
                        className="w-full h-48 object-contain bg-gray-100 rounded border hover:opacity-90 transition-opacity"
                        onError={(e) =>
                          (e.currentTarget.src =
                            "https://via.placeholder.com/400x200?text=Image+Load+Error")
                        }
                      />
                    </button>
                  </div>
                )}
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => handleUpdateStatus(profile.id, "approved")}
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 transition-colors font-medium"
                >
                  通过 (Approve)
                </button>
                <button
                  onClick={() => handleUpdateStatus(profile.id, "rejected")}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition-colors font-medium"
                >
                  拒绝 (Reject)
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
